import { isInFlightSubscriptionStatus } from "@intelligems/ig-types";

/**
 * `isTerminalSubscriptionStatus` answers the broad question — *did it stop?* — and decides
 * the `expired` account state, which exists to stop showing a returning merchant the
 * "start your free trial" screen. `findLapsedFreeTrial` answers the strict one — *was
 * it a trial that never converted?* — and alone decides what the Trial Ended card
 * claims. A merchant who paid and later cancelled satisfies the first and not the
 * second.
 */

/**
 * True when a subscription is dead and will never bill again.
 *
 * Expressed as the complement of `isInFlightSubscriptionStatus` ("present and not
 * terminal") so the terminal status list has exactly one home, in `ig-types`. The
 * presence check is the part that matters: an absent status is no evidence that a
 * subscription has ended, and without it an org whose subscription is `pending`
 * (awaiting external approval) or `paused` — alive, just not live, and so sorted into
 * `historical` all the same — would read as one that is over.
 */
export const isTerminalSubscriptionStatus = (
  status: string | null | undefined
): boolean => !!status && !isInFlightSubscriptionStatus(status);

export type FreeTrialHistoryCandidate = {
  status?: string | null;
  trialEnd?: number | null;
  endedAt?: number | null;
};

/**
 * Gap allowed between a subscription's trial end and the moment it stopped,
 * before it counts as having converted to paid.
 */
const FREE_TRIAL_LAPSE_TOLERANCE_SECONDS = 24 * 60 * 60;

export function findLapsedFreeTrial<T extends FreeTrialHistoryCandidate>(
  subscriptions: readonly T[],
  options?: { toleranceSeconds?: number }
): T | null {
  const tolerance =
    options?.toleranceSeconds ?? FREE_TRIAL_LAPSE_TOLERANCE_SECONDS;

  const lapsed = subscriptions.filter((subscription) => {
    const { endedAt, status, trialEnd } = subscription;

    return (
      trialEnd != null &&
      endedAt != null &&
      isTerminalSubscriptionStatus(status) &&
      endedAt <= trialEnd + tolerance
    );
  });

  if (lapsed.length === 0) {
    return null;
  }

  return lapsed.reduce((latest, subscription) =>
    (subscription.trialEnd ?? 0) > (latest.trialEnd ?? 0)
      ? subscription
      : latest
  );
}
